import { kml as kmlToGeoJSON } from '@tmcw/togeojson';
import JSZip from 'jszip';

export interface TalhaoImport {
  numero: string;
  nome: string;
  geojson: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  area_ha: number;
}

// Área geodésica (mesma fórmula do @turf/area, esfera WGS84). Preview no form —
// o valor autoritativo vem do PostGIS (ST_Area::geography) no submit.
const R = 6378137;
const rad = (d: number) => (d * Math.PI) / 180;

function ringAreaM2(ring: number[][]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let a = 0;
  for (let i = 0; i < n; i++) {
    const p1 = ring[i]!;
    const p2 = ring[(i + 1) % n]!;
    a += rad(p2[0]! - p1[0]!) * (2 + Math.sin(rad(p1[1]!)) + Math.sin(rad(p2[1]!)));
  }
  return Math.abs((a * R * R) / 2);
}
function polyAreaM2(rings: number[][][]): number {
  if (!rings.length) return 0;
  let area = ringAreaM2(rings[0]!);
  for (let i = 1; i < rings.length; i++) area -= ringAreaM2(rings[i]!);
  return Math.max(0, area);
}
function geomAreaHa(g: GeoJSON.Polygon | GeoJSON.MultiPolygon): number {
  let m2 = 0;
  if (g.type === 'Polygon') m2 = polyAreaM2(g.coordinates);
  else for (const poly of g.coordinates) m2 += polyAreaM2(poly);
  return Math.round((m2 / 10000) * 10000) / 10000;
}

// Lê um .kml e devolve um talhão por polígono (Polygon/MultiPolygon).
export function parseKml(text: string): TalhaoImport[] {
  const dom = new DOMParser().parseFromString(text, 'text/xml');
  const fc = kmlToGeoJSON(dom);
  const out: TalhaoImport[] = [];
  let i = 0;
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon')) continue;
    i++;
    const props = (f.properties ?? {}) as Record<string, unknown>;
    const nome = typeof props.name === 'string' ? props.name.trim() : '';
    const geom = g;
    out.push({
      numero: nome || `T${i}`,
      nome: '',
      geojson: geom,
      area_ha: geomAreaHa(geom),
    });
  }
  return out;
}

// Lê um arquivo .kml OU .kmz (zip com um .kml dentro) e devolve os talhões.
export async function parseKmlFile(file: File): Promise<TalhaoImport[]> {
  const kmz = /\.kmz$/i.test(file.name) || file.type === 'application/vnd.google-earth.kmz';
  if (kmz) {
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const entry = Object.values(zip.files).find((f) => !f.dir && /\.kml$/i.test(f.name));
    if (!entry) throw new Error('KMZ sem arquivo .kml');
    return parseKml(await entry.async('text'));
  }
  return parseKml(await file.text());
}
